/**
 * Workout Service
 *
 * Service for managing workout-related operations including CRUD operations,
 * workout sharing, and filtering. Extends the base
 * CrudService to provide workout-specific functionality with proper
 * validation and error handling.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { clearStartWorkoutOptionsCache } from '../utils/workout/start-options/start-answer';
import { validateShareResourceConfig } from '../validation/share.schemas';
import {
  validateWorkoutTemplate,
  type WorkoutTemplateInput,
} from '../validation/template.schemas';

import type {
  SearchRequest,
  ShareResourceConfig,
  PaginatedResponse,
  CreatedApiResponse,
  SuccessApiResponse,
  ID,
} from '../types';
import type { StartWorkoutOptions } from '../types/domain/start-options.types';
import type {
  WorkoutByExercise,
  WorkoutTemplate,
  WorkoutTemplateDetail,
} from '../types/domain/workout.types';

// =============================================================================
// WORKOUT SERVICE CLASS
// =============================================================================

/**
 * WorkoutService class providing comprehensive workout management functionality
 * Extends CrudService to inherit common CRUD operations and adds workout-specific methods
 */
export class WorkoutService extends CrudService<
  WorkoutTemplateDetail,
  WorkoutTemplate,
  WorkoutTemplateInput
> {
  constructor() {
    super({
      baseUrl: '/workout',
      enableCaching: true,
      cacheTTL: 5 * 60 * 1000, // 5 minutes
      cacheVariesByUnit: true, // set weights/volumes arrive in the user's unit
    });
  }

  /**
   * Every write that drops this service's cached reads also drops the
   * start-workout flow's remembered answer: its workouts are the ones the flow
   * offers. Without it the bolt opens on the old answer and holds the new one
   * back as a pending pill.
   */
  protected override invalidateCache(operations: string[], id?: ID): void {
    super.invalidateCache(operations, id);
    clearStartWorkoutOptionsCache();
  }

  // ---------------------------------------------------------------------------
  // CORE WORKOUT OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get workouts with advanced filtering and pagination
   * Supports filtering by difficulty, plan, status, and search terms
   *
   * @param params - Search and filter parameters
   * @returns Promise resolving to paginated workout list
   *
   * @example
   * ```typescript
   * const workouts = await workoutService.getWorkouts({
   *   difficulty_level: 'Intermediate',
   *   plan: 'strength-training',
   *   term: 'upper body',
   *   paginate: false, // the whole list; omit it for the API's first page
   * });
   * ```
   */
  public async getWorkouts(
    params: SearchRequest
  ): Promise<PaginatedResponse<WorkoutTemplate>> {
    try {
      // Validate and transform search parameters
      const queryParams = this.transformSearchParams(params);

      // One page (the API's default) unless the caller asks for everything
      if (params.paginate !== false) {
        return await this.getPaginated(queryParams);
      }

      // Everything, via getAll's paginate=false
      const results = await this.getAll(queryParams);

      // Transform to paginated response format for consistency
      return this.transformToPaginated(results);
    } catch (error) {
      console.error('Failed to fetch workouts:', error);
      rethrowTyped(error, 'Unable to fetch workouts. Please try again.');
    }
  }

  /**
   * List the user's workouts that currently contain a given exercise.
   *
   * GET /api/exercise/{guid}/workouts
   *
   * A plain read rather than a CrudService call: the route hangs off the
   * exercise, not the workout collection this service is scoped to, so there
   * is no context whose cache it belongs in.
   *
   * @param guid - Exercise GUID
   * @returns The workouts holding it, most recently performed first
   */
  public async getWorkoutsByExercise(
    guid: string
  ): Promise<WorkoutByExercise[]> {
    if (!guid) {
      throw new Error('Exercise GUID is required to find its workouts');
    }

    try {
      // Wrapped: the controller answers through the success macro, so the body
      // is `{ data: [...] }` rather than the array itself.
      const response = await apiClient.get<
        SuccessApiResponse<WorkoutByExercise[]>
      >(`/exercise/${guid}/workouts`);

      return response.data.data;
    } catch (error) {
      console.error(`Failed to fetch workouts for exercise ${guid}:`, error);
      rethrowTyped(
        error,
        'Unable to find the workouts for this exercise. Please try again.'
      );
    }
  }

  /**
   * Everything the start-workout sheet needs, in one request.
   *
   * GET /api/workout/start-options
   *
   * A plain read rather than a CrudService call: it is not one of the five
   * cached read operations and it answers for the whole account rather than for
   * a workout, so there is no key it belongs under. It is also the one request
   * the bolt makes — the sheet derives every frame it draws from this payload
   * and never asks a second question of the network.
   *
   * @returns The schedule facts, the suggestions and the whole workout list
   */
  public async getStartOptions(): Promise<StartWorkoutOptions> {
    try {
      // Wrapped: the controller returns a JsonResource, so the body is
      // `{ data: {...} }` rather than the options object itself.
      const response = await apiClient.get<
        SuccessApiResponse<StartWorkoutOptions>
      >('/workout/start-options');

      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch start-workout options:', error);
      rethrowTyped(
        error,
        'Unable to work out what you could start. Please try again.'
      );
    }
  }

  /**
   * Get detailed workout information by ID
   * Returns complete workout template with exercises and metadata
   *
   * @param id - Workout identifier
   * @returns Promise resolving to workout details
   *
   * @example
   * ```typescript
   * const workout = await workoutService.getWorkoutById('workout-123');
   * ```
   */
  public async getWorkoutById(id: ID): Promise<WorkoutTemplateDetail> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      const workout = await this.getById(id);
      return this.transformResponse(workout);
    } catch (error) {
      console.error(`Failed to fetch workout ${id}:`, error);
      rethrowTyped(
        error,
        `Unable to fetch workout details. Please check the workout ID and try again.`
      );
    }
  }

  /**
   * Create a new workout with validation
   * Validates workout data before sending to API
   *
   * @param data - Workout creation data
   * @returns Promise resolving to created workout
   *
   * @example
   * ```typescript
   * const newWorkout = await workoutService.createWorkout({
   *   name: 'Upper Body Strength',
   *   description: 'Focus on chest, shoulders, and arms',
   *   difficulty_level: 'intermediate',
   *   rest_duration: 90,
   *   plan: 'strength-training'
   * });
   * ```
   */
  public async createWorkout(
    data: WorkoutTemplateInput
  ): Promise<WorkoutTemplate> {
    try {
      // Validate workout data
      this.validateWorkoutData(data);

      // Transform data before sending to API
      const transformedData = this.transformRequest(data);

      return await this.create(transformedData);
    } catch (error) {
      console.error('Failed to create workout:', error);
      rethrowTyped(
        error,
        'Unable to create workout. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing workout (full replace)
   * Validates data and replaces the entire workout resource
   *
   * @param id - Workout identifier
   * @param data - Full workout data to replace existing
   * @returns Promise resolving to updated workout
   *
   * @example
   * ```typescript
   * const updatedWorkout = await workoutService.updateWorkout('workout-123', {
   *   name: 'Advanced Upper Body Strength',
   *   difficulty_level: 'hard',
   *   rest_duration: 90,
   *   exercises: [...]
   * });
   * ```
   */
  public async updateWorkout(
    id: ID,
    data: WorkoutTemplateInput
  ): Promise<WorkoutTemplate> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      // Validate workout data
      this.validateWorkoutData(data);

      // Transform data before sending to API
      const transformedData = this.transformRequest(data);

      return await this.update(id, transformedData);
    } catch (error) {
      console.error(`Failed to update workout ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update workout. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a workout by ID
   * Permanently removes the workout and all associated data
   *
   * @param id - Workout identifier
   * @returns Promise resolving when deletion is complete
   *
   * @example
   * ```typescript
   * await workoutService.deleteWorkout('workout-123');
   * ```
   */
  public async deleteWorkout(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete workout ${id}:`, error);
      rethrowTyped(error, 'Unable to delete workout. Please try again.');
    }
  }

  /**
   * Restore a soft-deleted workout by ID
   * Used to undo a recent deletion before the record is pruned
   *
   * @param id - Workout identifier
   * @returns Promise resolving when the restore completes
   *
   * @example
   * ```typescript
   * await workoutService.restoreWorkout('workout-123');
   * ```
   */
  public async restoreWorkout(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Workout ID is required');
    }

    try {
      await this.undoDelete('workout', id);
    } catch (error) {
      console.error(`Failed to restore workout ${id}:`, error);
      rethrowTyped(error, 'Unable to restore workout. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // ADVANCED WORKOUT OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Share a workout with another user
   * Creates a shareable link or direct share with specified configuration
   *
   * @param id - Workout identifier
   * @param config - Sharing configuration including recipient and options
   * @returns Promise resolving to share confirmation
   *
   * @example
   * ```typescript
   * const shareResult = await workoutService.shareWorkout({
   *   share_with: ['john_doe', 'jane_smith'],
   *   share_id: 'workout-123',
   *   share_type: 'workout',
   *   note: 'Try this workout!',
   *   include_notes: false,
   * });
   * ```
   */
  public async shareWorkout(data: ShareResourceConfig): Promise<void> {
    try {
      // Validate share configuration
      this.validateWorkoutShareConfig(data);

      await apiClient.post<CreatedApiResponse>('/share', data);
    } catch (error) {
      console.error(
        `Failed to share resource ${data.share_type} with ID, ${data.share_id}:`,
        error
      );
      rethrowTyped(
        error,
        'Unable to share workout. Please check your sharing configuration and try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION METHODS
  // ---------------------------------------------------------------------------

  /**
   * Validate workout data using Zod schema
   * Ensures data meets requirements before API calls
   *
   * @param data - Workout data to validate
   * @throws Error if validation fails
   */
  protected validateWorkoutData(data: WorkoutTemplateInput): void {
    try {
      validateWorkoutTemplate(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the workout", error);
      }
      throw new InvalidDataError(
        "Couldn't save the workout: the data is in an unexpected format"
      );
    }
  }

  /**
   * Validate share configuration
   * Ensures sharing parameters are valid and complete
   *
   * @param config - Share configuration to validate
   * @throws Error if validation fails
   */
  protected validateWorkoutShareConfig(config: ShareResourceConfig): void {
    if (config.share_type !== 'workout') {
      throw new InvalidDataError('Share type must be "workout"');
    }

    try {
      validateShareResourceConfig(config);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't share the workout", error);
      }
      throw new InvalidDataError(
        "Couldn't share the workout: the data is in an unexpected format"
      );
    }
  }

  /**
   * Transform request data before sending to API
   * Formats data according to API expectations
   *
   * @param data - Original request data
   * @returns Transformed request data
   */
  protected override transformRequest(
    data: Partial<WorkoutTemplateInput>
  ): Partial<WorkoutTemplateInput> {
    return {
      ...data,
    };
  }

  /**
   * Transform response data received from API
   * Formats data for frontend consumption
   *
   * @param data - Raw API response data
   * @returns Transformed response data
   */
  protected override transformResponse(
    data: WorkoutTemplateDetail
  ): WorkoutTemplateDetail {
    // Add any necessary response transformations here
    // For example, parsing dates or converting values
    return {
      ...data,
      // Ensure the roster + recent-sessions arrays exist
      exercises: data.exercises || [],
      recent_sessions: data.recent_sessions || [],
    };
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default workout service instance
 * Pre-configured and ready to use throughout the application
 */
export const workoutService = new WorkoutService();
